/**
 * SEO utility functions for generating SEO-friendly URLs and meta data
 */

export const PRIMARY_SEO_SITE_URL = 'https://eztopup.com.mm';
export const SECONDARY_SEO_SITE_URL = 'https://myeztopup.com';

export const SEO_SITE_ALIASES = [PRIMARY_SEO_SITE_URL, SECONDARY_SEO_SITE_URL] as const;

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/$/, '');

/**
 * Always use the primary public domain for canonicals / OG / structured data.
 * myeztopup.com 301-redirects here; API hosts stay on *.myeztopup.com.
 */
export const resolveSeoBaseUrl = (): string => PRIMARY_SEO_SITE_URL;

/**
 * Build an absolute SEO URL from a path using current/primary domain.
 */
export const buildSeoUrl = (path: string, baseUrl = resolveSeoBaseUrl()): string => {
  const normalizedBase = normalizeBaseUrl(baseUrl);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

/**
 * Convert category name to SEO-friendly slug
 * @param name - Category name
 * @returns SEO-friendly slug
 */
export const generateCategorySlug = (name: string): string => {
  // Myanmar to English category name mappings
  const myanmarToEnglishMap: Record<string, string> = {
    ဖုန်းငွေဖြည့်ကဒ်: 'mobile-topup',
    ဒေတာပက်ကေ့ချ်: 'data-package',
    'ပြည်တွင်း E-PIN': 'local-epins',
    လက်ဆောင်ကဒ်: 'egift-cards',
    မိုဘိုင်းဖုန်း: 'mobile-topup',
    ဂိမ်း: 'games',
    အင်တာနက်: 'internet',
    ဘီလ်များ: 'bills',
  };

  // Check if it's a Myanmar category name
  if (myanmarToEnglishMap[name]) {
    return myanmarToEnglishMap[name];
  }

  // For English names, generate slug normally
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Convert slug back to category ID for matching
 * @param slug - URL slug
 * @returns Possible category ID variations
 */
export const slugToCategoryId = (slug: string): string[] => {
  const variations = [
    slug, // Direct match
    slug.replace(/-/g, ''), // Remove hyphens
    slug.replace(/-/g, ' '), // Replace hyphens with spaces
    slug.replace(/-/g, '_'), // Replace hyphens with underscores
  ];

  // Add special mappings
  const specialMappings: Record<string, string> = {
    'gift-cards': 'egift-cards',
    'e-gift-cards': 'egift-cards',
    'mobile-top-up': 'mobile-topup',
    'mobile-recharge': 'mobile-topup',
    'data-plans': 'data-package',
    'data-bundles': 'data-package',
    'local-pins': 'local-epins',
    'e-pins': 'local-epins',
  };

  if (specialMappings[slug]) {
    variations.push(specialMappings[slug]);
  }

  return variations;
};

/**
 * Generate SEO-friendly category title
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns SEO-optimized title
 */
export const generateCategoryTitle = (categoryName: string, operatorName?: string): string => {
  const baseTitle = categoryName;
  const siteName = 'EZTopup';

  if (operatorName) {
    return `${baseTitle} for ${operatorName} | ${siteName}`;
  }

  return `${baseTitle} | ${siteName}`;
};

/**
 * Generate SEO-friendly category description
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns SEO-optimized description
 */
export const generateCategoryDescription = (
  categoryName: string,
  operatorName?: string,
): string => {
  const baseDescriptions: Record<string, string> = {
    'mobile-topup':
      'Instant mobile top-up and recharge services for all Myanmar operators. Fast, secure, and convenient digital phone credit.',
    'data-package':
      'Affordable data packages and internet bundles for all Myanmar networks. Stay connected with high-speed mobile data.',
    'local-epins':
      'Local E-PINs and digital vouchers for Myanmar. Secure electronic payment solutions for various services.',
    'egift-cards':
      'Digital gift cards and e-vouchers for Myanmar. Perfect gifts for friends and family with instant delivery.',
  };

  const categoryId = Object.keys(baseDescriptions).find(
    (id) =>
      categoryName.toLowerCase().includes(id.replace('-', ' ')) ||
      categoryName.toLowerCase().includes(id.replace('-', '')),
  );

  let description =
    baseDescriptions[categoryId || 'mobile-topup'] ||
    `${categoryName} services in Myanmar. Fast, secure, and convenient digital solutions.`;

  if (operatorName) {
    description = description
      .replace('all Myanmar operators', operatorName)
      .replace('all Myanmar networks', operatorName)
      .replace('Myanmar', `${operatorName} Myanmar`);
  }

  return description;
};

/**
 * Generate category-specific keywords
 * @param categoryName - Category name
 * @param operatorName - Optional operator name
 * @returns Comma-separated keywords
 */
export const generateCategoryKeywords = (categoryName: string, operatorName?: string): string => {
  const baseKeywords = [
    'Myanmar',
    'digital services',
    'instant delivery',
    'secure payment',
    'mobile services',
    // Myanmar-language terms
    'မြန်မာ',
    'ဒစ်ဂျစ်တယ်',
  ];

  const categoryKeywords: Record<string, string[]> = {
    'mobile-topup': [
      'mobile topup',
      'phone recharge',
      'mobile credit',
      'phone credit',
      'airtime',
      'prepaid recharge',
      // Myanmar-language
      'ဖုန်းငွေဖြည့်',
      'ဖုန်းဘေလ်ဖြည့်',
      'မိုဘိုင်းငွေဖြည့်',
      'MPT ငွေဖြည့်',
      'Atom ငွေဖြည့်',
      'U9 ငွေဖြည့်',
      'MyTel ငွေဖြည့်',
      'ဖုန်းကဒ်',
      'ဖုန်းဘေလ်',
    ],
    'data-package': [
      'data packages',
      'internet bundles',
      'mobile data',
      'data plans',
      'internet packages',
      'mobile internet',
      // Myanmar-language
      'ဒေတာဝယ်',
      'ဒေတာပက်ကေ့ချ်',
      'အင်တာနက်ပက်ကေ့ချ်',
      'မိုဘိုင်းဒေတာ',
      'MPT ဒေတာ',
      'Atom ဒေတာ',
    ],
    'local-epins': [
      'e-pins',
      'electronic pins',
      'digital vouchers',
      'local pins',
      'payment vouchers',
      // Myanmar-language
      'ပြည်တွင်းကဒ်',
      'ဒစ်ဂျစ်တယ်ကူပွန်',
    ],
    'egift-cards': [
      'gift cards',
      'e-gift cards',
      'digital gifts',
      'electronic vouchers',
      'gift vouchers',
      'prepaid cards',
      // Myanmar-language
      'လက်ဆောင်ကဒ်',
      'ဂိဖ်ကဒ်',
      'ဂစ်ကဒ်',
      'ဒစ်ဂျစ်တယ်လက်ဆောင်',
      'အွန်လိုင်းကဒ်ဝယ်',
    ],
  };

  const categoryId = Object.keys(categoryKeywords).find(
    (id) =>
      categoryName.toLowerCase().includes(id.replace('-', ' ')) ||
      categoryName.toLowerCase().includes(id.replace('-', '')),
  );

  const keywords = [...baseKeywords, ...(categoryKeywords[categoryId || 'mobile-topup'] || [])];

  if (operatorName) {
    keywords.push(operatorName, `${operatorName} Myanmar`, `${operatorName} ငွေဖြည့်`);
  }

  // Add Myanmar operators
  keywords.push('MPT', 'U9', 'Telenor', 'Atom', 'MyTel', 'MEC');

  return keywords.join(', ');
};

/**
 * Generate structured data for category page
 * @param categoryName - Category name
 * @param categorySlug - Category slug
 * @param description - Category description
 * @returns JSON-LD structured data
 */
export const generateCategoryStructuredData = (
  categoryName: string,
  categorySlug: string,
  description: string,
  baseUrl = resolveSeoBaseUrl(),
) => {
  const categoryUrl = buildSeoUrl(`/product/${categorySlug}`, baseUrl);

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: categoryName,
    description: description,
    url: categoryUrl,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Products`,
      description: description,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: buildSeoUrl('/', baseUrl),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Products',
          item: buildSeoUrl('/product', baseUrl),
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: categoryName,
          item: categoryUrl,
        },
      ],
    },
    provider: {
      '@type': 'Organization',
      name: 'EZTopup',
      url: buildSeoUrl('/', baseUrl),
    },
  };
};

/**
 * Generate WebSite schema with SearchAction for Google Sitelinks search box
 */
export const generateWebSiteStructuredData = (baseUrl = resolveSeoBaseUrl()) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'EZTopup',
  alternateName: 'မိုင်အီးဇီတော့ပ်',
  url: buildSeoUrl('/', baseUrl),
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${buildSeoUrl('/product', baseUrl)}?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: ['en', 'my'],
});

/**
 * Generate LocalBusiness schema targeting Myanmar market
 */
export const generateLocalBusinessStructuredData = (baseUrl = resolveSeoBaseUrl()) => ({
  '@context': 'https://schema.org',
  '@type': 'FinancialService',
  name: 'EZTopup',
  alternateName: 'မိုင်အီးဇီတော့ပ်',
  description:
    "Myanmar's trusted digital platform for instant mobile top-up, data packages, and e-gift cards. ဖုန်းငွေဖြည့်ခြင်း၊ ဒေတာပက်ကေ့ချ်နှင့် လက်ဆောင်ကဒ် ဝန်ဆောင်မှုများ။",
  url: buildSeoUrl('/', baseUrl),
  logo: buildSeoUrl('/logo.svg', baseUrl),
  image: buildSeoUrl('/og-image.jpg', baseUrl),
  telephone: '+95-',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'MM',
    addressRegion: 'Yangon',
    addressLocality: 'Yangon',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '16.8661',
    longitude: '96.1951',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Myanmar',
    '@id': 'https://www.wikidata.org/wiki/Q836',
  },
  priceRange: '1,000 MMK - 100,000 MMK',
  currenciesAccepted: 'MMK',
  paymentAccepted: 'Credit Card, Debit Card, Wave Pay, KPay',
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00',
    closes: '23:59',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Digital Mobile Services Myanmar',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Mobile Top-up',
          description: 'Instant mobile phone credit for MPT, Atom, U9, MyTel, MEC',
          serviceType: 'Mobile Recharge',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Data Packages',
          description: 'Mobile internet data bundles for all Myanmar networks',
          serviceType: 'Data Package',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'E-Gift Cards',
          description: 'Digital gift cards and prepaid cards for Myanmar',
          serviceType: 'Gift Card',
        },
      },
    ],
  },
  sameAs: [
    SECONDARY_SEO_SITE_URL + '/',
    'https://www.facebook.com/eztopupmm',
    'https://www.instagram.com/eztopupmm',
  ],
});

/**
 * Generate FAQPage schema for common Myanmar topup questions
 */
export const generateFAQStructuredData = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I top up my mobile phone in Myanmar?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Log in to EZTopup, select Mobile Top-up, choose your operator (MPT, Atom, U9, MyTel, or MEC), enter your phone number and amount, then complete payment. Your phone credit is delivered instantly.',
      },
    },
    {
      '@type': 'Question',
      name: 'ဖုန်းငွေဖြည့်ရန် ဘယ်လိုလုပ်ရမလဲ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EZTopup တွင် Login ဝင်ပါ၊ Mobile Top-up ကိုရွေးပြီး သင့်ဖုန်းနံပါတ်နှင့် ဖြည့်မည့်ပမာဏကို ထည့်သွင်းပါ။ ငွေပေးချေပြီးနောက် ဖုန်းဘေလ် ချက်ချင်းရောက်ပါမည်။',
      },
    },
    {
      '@type': 'Question',
      name: 'What operators does EZTopup support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EZTopup supports all major Myanmar mobile operators: MPT, Atom, U9 (formerly Ooredoo), MyTel, and MEC (Myanmar Economic Corporation). Data packages are also available for all networks.',
      },
    },
    {
      '@type': 'Question',
      name: 'MPT, Atom, U9, MyTel ဖုန်းတွေကို ငွေဖြည့်လို့ရပါသလား?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ဟုတ်ကဲ့၊ EZTopup မှ MPT, Atom, MyTel, MEC အပါအဝင် မြန်မာ့ မိုဘိုင်းကုမ္ပဏီအားလုံးကို ငွေဖြည့်နိုင်ပါသည်။',
      },
    },
    {
      '@type': 'Question',
      name: 'Is mobile top-up delivered instantly?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, all mobile top-ups on EZTopup are processed instantly. Once payment is confirmed, your phone credit is delivered within seconds.',
      },
    },
    {
      '@type': 'Question',
      name: 'What payment methods are accepted?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EZTopup accepts multiple payment methods including credit/debit cards, Wave Pay (KBZ Pay), KPay, and other popular Myanmar mobile wallets.',
      },
    },
    {
      '@type': 'Question',
      name: 'ဂိဖ်ကဒ် / E-Gift Card တွေ ဝယ်လို့ရပါသလား?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ဟုတ်ကဲ့၊ EZTopup တွင် မြန်မာနိုင်ငံအတွက် E-Gift Card နှင့် Prepaid Card အမျိုးမျိုးကို ဝယ်ယူနိုင်ပါသည်။',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the minimum and maximum top-up amount?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The minimum top-up amount is 1,000 MMK and the maximum varies by operator. Most operators allow top-ups up to 100,000 MMK per transaction.',
      },
    },
  ],
});
